import React, { useLayoutEffect, useRef } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import LocationCategoriesManagementPage from './LocationCategoriesManagementPage';
import DocumentCategoriesManagementPage from './DocumentCategoriesManagementPage';
import OperatingCostCategoriesManagementPage from './OperatingCostCategoriesManagementPage';
import BuildingGroupTagsManagementPage from './BuildingGroupTagsManagementPage';
import './CategoriesManagementPage.css';

type CategoryTab = 'location' | 'document' | 'operating-cost' | 'building-groups';

const VALID_TABS: CategoryTab[] = ['location', 'document', 'operating-cost', 'building-groups'];

const TAB_CONFIG: { id: CategoryTab; label: string; path: string }[] = [
    { id: 'location', label: 'Platskategorier', path: '/settings/categories/location' },
    { id: 'document', label: 'Dokumentkategorier', path: '/settings/categories/document' },
    { id: 'operating-cost', label: 'Driftkostnadskategorier', path: '/settings/categories/operating-cost' },
    { id: 'building-groups', label: 'Byggnadsgrupper', path: '/settings/categories/building-groups' },
];

const CategoriesManagementPage: React.FC = () => {
    const location = useLocation();

    // Extract tab from URL path: /settings/categories/location -> 'location'
    const pathParts = location.pathname.split('/');
    const tabFromUrl = pathParts[pathParts.length - 1];

    // Validate and determine active tab
    const isValidTab = VALID_TABS.includes(tabFromUrl as CategoryTab);
    const activeTab: CategoryTab = isValidTab ? (tabFromUrl as CategoryTab) : 'location';

    // On phones the tab bar scrolls sideways; bring the active tab into view
    // (scrollLeft only, so the page itself does not jump).
    const tabBarRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        const bar = tabBarRef.current;
        const active = bar?.querySelector<HTMLElement>('.categories-tab.active');
        if (bar && active && bar.scrollWidth > bar.clientWidth) {
            bar.scrollLeft = active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2;
        }
    }, [activeTab]);

    // Redirect to default tab if at /settings/categories with no subpath
    if (location.pathname === '/settings/categories' || location.pathname === '/settings/categories/') {
        return <Navigate to="/settings/categories/location" replace />;
    }

    return (
        <div className="categories-management-page">
            {/* Tab bar */}
            <div className="categories-tab-bar" ref={tabBarRef}>
                {TAB_CONFIG.map((tab) => (
                    <Link
                        key={tab.id}
                        to={tab.path}
                        className={`categories-tab ${activeTab === tab.id ? 'active' : ''}`}
                        aria-current={activeTab === tab.id ? 'page' : undefined}
                    >
                        {tab.label}
                    </Link>
                ))}
            </div>

            {/* Tab content */}
            <div className="categories-tab-content">
                {activeTab === 'location' && (
                    <div style={{ maxWidth: 800 }}>
                        <LocationCategoriesManagementPage />
                    </div>
                )}
                {activeTab === 'document' && (
                    <DocumentCategoriesManagementPage />
                )}
                {activeTab === 'operating-cost' && (
                    <OperatingCostCategoriesManagementPage />
                )}
                {activeTab === 'building-groups' && (
                    <BuildingGroupTagsManagementPage />
                )}
            </div>
        </div>
    );
};

export default CategoriesManagementPage;
